import PropTypes from '@root/vendor/prop-types';
import { useEffect, useRef } from '@root/vendor/react';

const TARGET_SELECTOR = '[data-testid="joinroot-link-button"]';
const LISTENER_BOUND_DATASET_KEY = 'agencyOnboardingGetAppointedHandlerBound';

function isTargetElement(element, normalizedButtonText) {
  const hrefValue = element.getAttribute('href') || '';

  return element.textContent?.trim().toLowerCase() === normalizedButtonText
    && hrefValue.includes('/#');
}

export default function AgencyOnboardingClickInjector({ buttonText, onClick }) {
  const onClickRef = useRef(onClick);
  const normalizedButtonText = buttonText.trim().toLowerCase();

  useEffect(() => {
    onClickRef.current = onClick;
  }, [onClick]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const clickHandlersByElement = new Map();

    const attachHandler = (element) => {
      if (!(element instanceof HTMLElement)) {
        return;
      }

      if (element.dataset[LISTENER_BOUND_DATASET_KEY] === 'true') {
        return;
      }

      if (!isTargetElement(element, normalizedButtonText)) {
        return;
      }

      const clickHandler = (event) => {
        event.preventDefault();
        onClickRef.current(event);
      };

      // Capture phase guarantees we cancel default anchor navigation before it can scroll the page.
      element.addEventListener('click', clickHandler, true);
      element.dataset[LISTENER_BOUND_DATASET_KEY] = 'true';
      clickHandlersByElement.set(element, clickHandler);
    };

    const attachHandlersToAllMatchingButtons = () => {
      document.querySelectorAll(TARGET_SELECTOR).forEach((element) => {
        attachHandler(element);
      });
    };

    attachHandlersToAllMatchingButtons();

    const observer = new MutationObserver(() => {
      attachHandlersToAllMatchingButtons();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      clickHandlersByElement.forEach((clickHandler, element) => {
        element.removeEventListener('click', clickHandler, true);
        delete element.dataset[LISTENER_BOUND_DATASET_KEY];
      });
      clickHandlersByElement.clear();
    };
  }, [normalizedButtonText]);

  return null;
}

AgencyOnboardingClickInjector.propTypes = {
  buttonText: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};
